import { useEffect, useState } from "react"
import { handleSsoCallback, redirectToSso } from "@alascorporation/api-client"
import PageMeta from "../../components/common/PageMeta"
import AuthLayout from "./AuthPageLayout"
import { ssoConfig } from "../../lib/sso"
import { clearAuthSession, getCurrentUser, initSentryUser, isStaffProfile } from "../../lib/auth"

export default function AuthCallback() {
  const [error, setError] = useState("")

  useEffect(() => {
    handleSsoCallback(ssoConfig)
      .then(({ returnTo, user }) => {
        if (!user) {
          setError("La API no devolvió una sesión válida")
          return
        }

        // handleSsoCallback already wrote {token, user} to localStorage —
        // read it back via getCurrentUser() rather than the `user` above,
        // which is typed as the shared package's generic HubSessionUser and
        // doesn't carry gestion-specific fields like mustChangePassword.
        const stored = getCurrentUser()

        // gestion is staff-only — a real ecosystem account (student, say)
        // can still complete the hub login, so the role gate that used to
        // live in SignInForm's onSubmit now has to run here instead, after
        // the fact, and undo the session handleSsoCallback already wrote.
        if (!stored?.role || !isStaffProfile(stored.role)) {
          clearAuthSession()
          setError("Esta cuenta no tiene acceso a gestión")
          return
        }

        initSentryUser()

        // Hard navigation (not react-router's navigate): several parts of
        // the app read the session straight from localStorage on demand
        // rather than through a hydrated store, but a full reload is still
        // the simplest way to guarantee every already-mounted component
        // (notably ProtectedRoute) re-evaluates isStaffAuthenticated() with
        // the session this callback just wrote.
        window.location.href = stored.mustChangePassword ? "/change-password" : returnTo || "/"
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : "No se pudo completar el inicio de sesión")
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      <PageMeta title="Iniciando sesión | Gestión SateLab" description="Completando el inicio de sesión." />
      <AuthLayout>
        <div className="flex flex-col justify-center flex-1 w-full max-w-md mx-auto text-center">
          {error ? (
            <>
              <p role="alert" aria-live="assertive" className="text-sm font-medium text-error-500">{error}</p>
              <button
                type="button"
                onClick={() => void redirectToSso(ssoConfig)}
                className="mt-4 text-sm font-medium text-brand-500 hover:underline"
              >
                Volver a intentar
              </button>
            </>
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400">Completando inicio de sesión...</p>
          )}
        </div>
      </AuthLayout>
    </>
  )
}
